import { forwardRef, useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import ClipCard, { ClipCardProps } from "../../Cards/ClipCard/clip-card";
import { Button, CircularProgress, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { JWPMarkerTimecode } from "../../../Utils/Timecode/ReactPlayer";
import { ChevronRightOutlined, TimerOutlined } from "@mui/icons-material";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { assetService, cacheService, orderService, userService } from "../../../Core/Api/services";
import { SearchCard } from "../../Cards/SearchCard/SearchCard";
import {
  CreateSimpleOrderRequest,
  SimpleOrderType,
  TranscodeSimpleOrderSettings,
} from "../../../Core/Api/Types/create-simple-order-request";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { enqueueSnackbar } from "notistack";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";

export type ModalDownloadClipElementProps = {
  clipId: number;
  onSuccess?: () => void;
};

export type ModalDownloadClipProps = CommonModalProps<ModalDownloadClipElementProps>;

export const ModalDownloadClip = forwardRef<any, ModalDownloadClipProps>((props, ref) => {
  const { closeModal, clipId, ...modalProps } = props;
  const [clipObj, setClipObj] = useState<DbAssetObjectInfos | null>(null);
  const [quality, setQuality] = useState<"proxy" | "native" | "">("");
  const [loading, setLoading] = useState<boolean>();
  const userEmail = cacheService.GetUserInfo()?.email;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const { t } = useTranslation();

  const handleCancelClick = () => {
    closeModal();
  };

  const handleOrderClip = () => {
    if (!clipObj) return;

    const request = new CreateSimpleOrderRequest({
      type: SimpleOrderType.FileTranscode,
      settings: {
        $type: "VDM.AirLab.OrderService.Api.Models.TranscodeSettings, VDM.AirLab.OrderService.Api",
        objectId: clipObj.id,
        comment: "",
        recipientEmails: [userEmail ?? ""],
        play: true,
        proxy: quality === "proxy",
      } as TranscodeSimpleOrderSettings,
    });

    orderService.CreateSimpleOrdder(request).then((response) => {
      console.log(response);
      if (response.success) {
        closeModal();
        enqueueCustomSnackbar(t("alert:success.clipOrderedConfirm", { email: userEmail }), "success");
        modalProps.onSuccess && modalProps.onSuccess();
      } else {
        enqueueCustomSnackbar(response.message!, "error");
      }
    });
  };

  useEffect(() => {
    async function fetchClip() {
      setLoading(true);
      if (clipId && clipId > 0) {
        try {
          const clip = await assetService.GetObjectLightById(clipId);
          if (clip.graph) setClipObj(clip.graph);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    }

    fetchClip();
  }, [clipId]);

  const _qualitySelect = useMemo(() => {
    return (
      <Select
        displayEmpty
        id="demo-simple-select"
        value={quality}
        onChange={(e) => setQuality(e.target.value as "proxy" | "native" | "")}
      >
        <MenuItem value={""}>{t("menu:item.selectQuality")}</MenuItem>
        <MenuItem value={"proxy"}>{t("menu:item.downloadLowResolution")}</MenuItem>
        <MenuItem value={"native"}>{t("menu:item.downloadNativeSource")}</MenuItem>
      </Select>
    );
  }, [quality]);

  return (
    <Modal
      title={t("modal:modalDownloadClip.title")}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button onClick={handleOrderClip} disabled={!clipObj || quality === ""} variant="contained">
            {t("button:orderDownload")}
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <CircularProgress size={36} />
      ) : (
        <>
          {clipObj && (
            <Stack gap={2}>
              <>
                <SearchCard asset={clipObj} endAdornment={_qualitySelect} />
                <Typography variant="body1" color="text.secondary">
                  {t("modal:modalDownloadClip.notice", { userEmail: userEmail })}
                </Typography>
              </>
            </Stack>
          )}
        </>
      )}
    </Modal>
  );
});
